import { useCallback, useRef, useState } from "react";

import { ChevronSelectorVerticalDown } from "icons";
import { useTranslate } from "localization";
import { Button } from "minimum-ui/core";
import { color } from "minimum-ui/tokens";
import styled, { css } from "styled-components";
import { H4R2 } from "typography/headers";

import { useClickOutside } from "ui/hooks";

type BulkActionDropdownItem = {
  title: string;
  onClick: () => void;
  disabled?: boolean;
};

const BulkActionDropdown = ({
  dropdownItems,
  disabled,
  loading,
}: {
  dropdownItems: BulkActionDropdownItem[];
  disabled?: boolean;
  loading?: boolean;
}) => {
  const { translate } = useTranslate();

  const bulkActionDropdownRef = useRef<HTMLDivElement>(null);
  const bulkActionDropdownListRef = useRef<HTMLDivElement>(null);
  const [bulkActionDropdown, setBulkActionDropdown] = useState<boolean>(false);

  const onClose = useCallback(() => {
    setBulkActionDropdown(false);
  }, []);

  useClickOutside(bulkActionDropdownListRef, onClose, bulkActionDropdownRef);

  return (
    <BulkActionWrapper ref={bulkActionDropdownRef}>
      <BulkActionButton
        size="large"
        variant="contained"
        disabled={disabled}
        loading={loading}
        onClick={() => setBulkActionDropdown(!bulkActionDropdown)}
      >
        {translate("bulkAction")}
        <DropdownIconDown size={18} />
      </BulkActionButton>
      {bulkActionDropdown && (
        <BulkActionDrowdownList ref={bulkActionDropdownListRef}>
          {dropdownItems.map((dropdownItem: BulkActionDropdownItem) => {
            return (
              <BulkActionDrowdownListItem
                key={dropdownItem.title}
                $disabled={dropdownItem.disabled}
                onClick={
                  dropdownItem.disabled
                    ? undefined
                    : () => {
                        dropdownItem.onClick();
                        setBulkActionDropdown(!bulkActionDropdown);
                      }
                }
              >
                <BulkActionDrowdownListItemContent>
                  <H4R2>{dropdownItem.title}</H4R2>
                </BulkActionDrowdownListItemContent>
              </BulkActionDrowdownListItem>
            );
          })}
        </BulkActionDrowdownList>
      )}
    </BulkActionWrapper>
  );
};

const BulkActionWrapper = styled.div`
  position: relative;
`;
const BulkActionButton = styled(Button)`
  margin-left: 8px;
`;
const DropdownIconDown = styled(ChevronSelectorVerticalDown)`
  margin-top: -8px;
`;
const BulkActionDrowdownList = styled.div`
  position: absolute;
  right: -1px;
  min-width: 220px;
  background: white;
  border: 1px solid ${color.foreground.disabled};
  border-radius: 4px;
`;
const BulkActionDrowdownListItem = styled.div<{
  $disabled?: boolean;
}>`
  position: relative;
  padding: 8px 16px;
  color: ${color.neutral[500]};
  cursor: pointer;

  &:hover {
    background: ${color.neutral[200]};
    border-radius: 4px;
  }

  ${(props) =>
    props.$disabled &&
    css`
      cursor: not-allowed;
      color: ${color.neutral[200]};

      * {
        color: ${color.neutral[300]};
      }

      &:hover {
        background: inherit;
      }
    `}
`;
const BulkActionDrowdownListItemContent = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

export default BulkActionDropdown;
